import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc, 
  query, 
  orderBy, 
  limit,
  where,
  updateDoc
} from 'firebase/firestore';
import { db } from './firebase';

// Collection references
const usersRef = collection(db, 'users');

// Get all users with pagination
export const getAllUsers = async (page = 1, pageSize = 20) => {
  try {
    console.log('Fetching all users...');
    
    // Get all user documents
    const usersQuery = query(usersRef, orderBy('createdAt', 'desc'), limit(pageSize));
    const usersSnapshot = await getDocs(usersQuery);
    
    if (usersSnapshot.empty) {
      console.log('No users found in database');
      return { users: [], hasMore: false };
    }
    
    console.log(`Found ${usersSnapshot.size} total user documents`);
    
    const users = [];
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      users.push({
        id: doc.id,
        ...userData,
        // Ensure consistent date format
        createdAt: userData.createdAt ? 
                  (typeof userData.createdAt === 'string' ? userData.createdAt : new Date().toISOString()) 
                  : new Date().toISOString()
      });
    });
    
    // For simplicity, we're not implementing real pagination in this demo
    return { 
      users,
      hasMore: false,
      page,
      totalCount: users.length
    };
  } catch (error) {
    console.error('Error getting users:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (userId) => {
  if (!userId) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      // User doesn't exist in Firestore, check localStorage
      const localUserData = JSON.parse(localStorage.getItem('userProfiles') || '{}')[userId];
      if (localUserData) {
        return {
          id: userId,
          ...localUserData,
          source: 'local'
        };
      }
      return null;
    }
    
    return {
      id: userId,
      ...userDoc.data(),
      source: 'firestore'
    };
  } catch (error) {
    console.error('Error getting user:', error);
    
    // Try from local storage as fallback
    const localUserData = JSON.parse(localStorage.getItem('userProfiles') || '{}')[userId];
    if (localUserData) {
      return {
        id: userId,
        ...localUserData,
        source: 'local'
      };
    }
    
    throw error;
  }
};

// Update user status (block/unblock)
export const updateUserStatus = async (userId, isBlocked) => {
  if (!userId) throw new Error('User ID is required');
  
  try {
    const userRef = doc(db, 'users', userId);
    
    // First check if user exists
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        id: userId,
        isBlocked,
        updatedAt: new Date().toISOString()
      });
    } else {
      // Update existing user
      await updateDoc(userRef, {
        isBlocked,
        updatedAt: new Date().toISOString()
      });
    }
    
    // Also update localStorage for offline access
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    localUserProfiles[userId] = {
      ...localUserProfiles[userId],
      isBlocked,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('userProfiles', JSON.stringify(localUserProfiles));
    
    return {
      id: userId,
      isBlocked,
      success: true
    };
  } catch (error) {
    console.error('Error updating user status:', error);
    throw error;
  }
};

// Update or create user profile
export const updateUserProfile = async (userId, profileData) => {
  if (!userId) throw new Error('User ID is required');
  
  try {
    const userRef = doc(db, 'users', userId);
    
    // First check if user exists
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // Create user document if it doesn't exist
      await setDoc(userRef, {
        id: userId,
        ...profileData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    } else {
      // Update existing user
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: new Date().toISOString()
      });
    }
    
    // Also update localStorage for offline access
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    localUserProfiles[userId] = {
      ...localUserProfiles[userId],
      ...profileData,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem('userProfiles', JSON.stringify(localUserProfiles));
    
    return {
      id: userId,
      ...profileData,
      success: true
    };
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

// Find users by username (for @mentions)
export const findUsersByUsername = async (searchQuery, limit = 5) => {
  if (!searchQuery || searchQuery.trim() === '') return [];
  
  try {
    // First, try the exact username match in Firestore
    const exactQuery = query(
      usersRef,
      where('username', '==', searchQuery),
      limit(1)
    );
    
    const exactSnapshot = await getDocs(exactQuery);
    
    // Then, try partial matches based on displayName
    const partialQuery = query(
      usersRef,
      where('displayName', '>=', searchQuery),
      where('displayName', '<=', searchQuery + '\uf8ff'),
      limit(limit)
    );
    
    const partialSnapshot = await getDocs(partialQuery);
    
    // Combine results
    const results = [];
    
    // Add exact matches first
    exactSnapshot.forEach(doc => {
      const userData = doc.data();
      results.push({
        id: doc.id,
        ...userData
      });
    });
    
    // Then add partial matches (avoiding duplicates)
    partialSnapshot.forEach(doc => {
      if (!results.some(user => user.id === doc.id)) {
        const userData = doc.data();
        results.push({
          id: doc.id,
          ...userData
        });
      }
    });
    
    return results;
  } catch (error) {
    console.error('Error finding users by username:', error);
    return [];
  }
};

// Check if username is available
export const isUsernameAvailable = async (username) => {
  if (!username || username.trim() === '') return false;
  
  try {
    const usernameQuery = query(
      usersRef,
      where('username', '==', username.toLowerCase()),
      limit(1)
    );
    
    const snapshot = await getDocs(usernameQuery);
    
    return snapshot.empty;
  } catch (error) {
    console.error('Error checking username availability:', error);
    return false;
  }
};

// Check if a user is blocked
export const isUserBlocked = async (userId) => {
  if (!userId) return false;
  
  try {
    // First check Firebase
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.isBlocked === true;
    }
    
    // If not in Firebase, check localStorage
    const localUserProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
    return localUserProfiles[userId]?.isBlocked === true;
  } catch (error) {
    console.error('Error checking if user is blocked:', error);
    
    // Default to not blocked on error
    return false;
  }
};